import { useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Lock, ArrowRight, Pencil, Check, PlayCircle, ImageIcon, X, Save, Undo2, Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { ThreadLayer, CONN_COLORS } from "@/components/pms/ThreadLayer";
import CursorGuide from "@/components/pms/CursorGuide";
import UnitTypesSection from "@/components/pms/UnitTypesSection";

export default function Phase1Blocking({ data, reload, onDone }) {
  const { property, units, links, unit_types = [], can_edit_setup } = data;
  const isHotel = (property.category || "homestay").toLowerCase() === "hotel";
  const isDraft = (property.status || "draft").toLowerCase() !== "active";
  const ref = useRef(null);
  const [sel, setSel] = useState(null);
  const [ai, setAi] = useState([]);
  const [busyAi, setBusyAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [guideN, setGuideN] = useState(0);
  const [showExample, setShowExample] = useState(false);
  const [adminOverride, setAdminOverride] = useState(false);
  // Inline Add-unit input — kept separate from staged renames because
  // adding a unit hits the server right away (it needs a server-generated
  // uid before the host can start connecting it in a blocking group).
  const [addingName, setAddingName] = useState("");
  const [addingBusy, setAddingBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // ── Batch-edit staging (used only when adminOverride or !phase1_done) ──
  const [stagedRenames, setStagedRenames] = useState({}); // { uid: newName }
  const [stagedNewLinks, setStagedNewLinks] = useState([]); // [{a, b, ai}]
  const [stagedDeletedLinks, setStagedDeletedLinks] = useState(new Set()); // set of server link ids

  const locked = property.phase1_done && !adminOverride;
  const editMode = !locked; // when true, edits are staged, not sent immediately

  const stagedCount = Object.keys(stagedRenames).length + stagedNewLinks.length + stagedDeletedLinks.size;
  const dirty = stagedCount > 0;

  const unitName = (uid) => {
    if (stagedRenames[uid] !== undefined) return stagedRenames[uid];
    return units.find((u) => u.id === uid)?.name || "?";
  };

  const displayLinks = useMemo(() => {
    return links.filter((l) => !stagedDeletedLinks.has(l.id));
  }, [links, stagedDeletedLinks]);

  const stageRename = (uid) => {
    const nm = editName.trim();
    setEditingId(null);
    if (!nm) return;
    const current = units.find((u) => u.id === uid)?.name;
    setStagedRenames((s) => {
      const n = { ...s };
      if (nm === current) delete n[uid]; else n[uid] = nm;
      return n;
    });
  };

  const stageNewLink = (a, b, isAi) => {
    // Prevent dup vs server (that's not yet-deleted) or vs already-staged
    const dupOnServer = links.some((l) => !stagedDeletedLinks.has(l.id) &&
      ((l.a === a && l.b === b) || (l.a === b && l.b === a)));
    const dupStaged = stagedNewLinks.some((l) => (l.a === a && l.b === b) || (l.a === b && l.b === a));
    if (dupOnServer || dupStaged) return toast.error("These units are already connected");
    setStagedNewLinks((s) => [...s, { a, b, ai: !!isAi }]);
    setAi((s) => s.filter((x) => !(x.a === a && x.b === b)));
  };

  const stageDeleteLink = (link_id) => {
    setStagedDeletedLinks((s) => new Set(s).add(link_id));
  };

  const removeStagedNewLink = (a, b) => {
    setStagedNewLinks((s) => s.filter((l) => !((l.a === a && l.b === b) || (l.a === b && l.b === a))));
  };

  const undoDeleteLink = (link_id) => {
    setStagedDeletedLinks((s) => {
      const n = new Set(s); n.delete(link_id); return n;
    });
  };

  const discardAll = () => {
    setStagedRenames({}); setStagedNewLinks([]); setStagedDeletedLinks(new Set()); setAi([]);
    toast.info("All staged edits discarded");
  };

  // Add-unit handler — direct POST (no staging). After success we reload
  // the parent so the new unit appears in the list with a server-side id
  // ready for immediate linking in a blocking group.
  const addUnit = async () => {
    const nm = addingName.trim();
    if (addingBusy) return;
    setAddingBusy(true);
    try {
      const { data: r } = await api.post(`/pms/properties/${property.id}/units`, { name: nm });
      setAddingName("");
      toast.success(r.channex_provisioned
        ? `Unit "${r.unit.name}" added · provisioning to Channex now`
        : `Unit "${r.unit.name}" added`);
      await reload();
    } catch (e) {
      // 402 = add-on payment required for a live property
      if (e?.response?.status === 402) {
        const d = e.response.data?.detail || {};
        toast.error(d.message || "Add-on payment required before adding a unit to a live property");
      } else {
        toast.error(errMsg(e));
      }
    }
    setAddingBusy(false);
  };

  // Delete-unit handler — only reachable for draft properties (UI hides
  // the button on active properties, and the backend 403s on live too).
  const removeUnit = async (u) => {
    if (!window.confirm(`Remove unit "${unitName(u.id)}" from this property?\n\nThis unit's blocking-group threads and any OTA mappings will be deleted too. Cannot be undone.`)) return;
    setRemovingId(u.id);
    try {
      await api.delete(`/pms/units/${u.id}`);
      // Drop any staged link/rename that referenced this unit so Save
      // doesn't fail with a 404 later.
      setStagedNewLinks((s) => s.filter((l) => l.a !== u.id && l.b !== u.id));
      setStagedRenames((s) => { const n = { ...s }; delete n[u.id]; return n; });
      toast.success(`Unit "${u.name}" removed`);
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
    setRemovingId(null);
  };

  const saveAll = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      for (const [uid, nm] of Object.entries(stagedRenames)) {
        await api.patch(`/pms/units/${uid}`, { name: nm });
      }
      for (const link_id of stagedDeletedLinks) {
        await api.delete(`/pms/links/${link_id}`);
      }
      for (const l of stagedNewLinks) {
        await api.post(`/pms/properties/${property.id}/links`, { a: l.a, b: l.b, ai: l.ai });
      }
      toast.success(`${stagedCount} change${stagedCount === 1 ? "" : "s"} saved · broadcast to every OTA`);
      setStagedRenames({}); setStagedNewLinks([]); setStagedDeletedLinks(new Set());
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const clickUnit = (id) => {
    if (locked) return;
    if (!sel) return setSel(id);
    if (sel === id) return setSel(null);
    stageNewLink(sel, id, false);
    setSel(null);
  };

  const suggest = async () => {
    setBusyAi(true);
    try {
      const { data: r } = await api.post("/pms/ai/suggest", { property_id: property.id, phase: "blocking" });
      const fresh = r.suggestions.filter((s) => {
        const dupServer = links.some((l) => !stagedDeletedLinks.has(l.id) && ((l.a === s.a && l.b === s.b) || (l.a === s.b && l.b === s.a)));
        const dupStaged = stagedNewLinks.some((l) => (l.a === s.a && l.b === s.b) || (l.a === s.b && l.b === s.a));
        return !dupServer && !dupStaged;
      });
      setAi(fresh);
      toast[fresh.length ? "success" : "info"](fresh.length ? `AI proposed ${fresh.length} blocking connection(s) — review and accept` : "AI found no same-space units to suggest");
    } catch (e) { toast.error(errMsg(e)); }
    setBusyAi(false);
  };

  const complete = async () => {
    if (dirty) {
      await saveAll();
      if (Object.keys(stagedRenames).length || stagedNewLinks.length || stagedDeletedLinks.size) return; // save failed, don't lock
    }
    setBusy(true);
    try {
      await api.post(`/pms/properties/${property.id}/phase1-complete`);
      toast.success("Phase 1 locked — listing mapping unlocked");
      await reload();
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  // Build threads with pending-add (dashed) and pending-delete (strike-through)
  const threadLinks = [
    ...displayLinks.map((l, i) => ({ key: l.id, from: `unit-${l.a}`, to: `unit-${l.b}`, loop: true,
      color: CONN_COLORS[i % CONN_COLORS.length], dashed: false,
      onRemove: editMode ? () => stageDeleteLink(l.id) : null })),
    ...stagedNewLinks.map((l, i) => ({ key: `new-${l.a}-${l.b}`, from: `unit-${l.a}`, to: `unit-${l.b}`, loop: true,
      color: "#10B981", dashed: true, onRemove: () => removeStagedNewLink(l.a, l.b) })),
    ...ai.map((s, i) => ({ key: `ai-${i}`, from: `unit-${s.a}`, to: `unit-${s.b}`, loop: true,
      color: "#4F46E5", dashed: true, onAccept: () => stageNewLink(s.a, s.b, true),
      onDismiss: () => setAi((x) => x.filter((_, j) => j !== i)) })),
  ];

  const doneBtn = property.phase1_done && adminOverride;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 reveal">
      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Phase 1 — connect units that share a physical space</h2>
            <p className="text-xs text-slate-500 mt-1">Tap one unit, then another. Booking any unit in a group auto-blocks the rest. Rename any unit (pencil icon) — your name is its <b>primary identity</b>; Atiti, Airbnb and Goibibo may each call it something else, all threads lead back here.</p>
          </div>
          {!locked && (
            <div className="shrink-0 flex items-center gap-2">
              <button data-testid="see-example-button" onClick={() => setShowExample(true)}
                className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500 hover:text-slate-700 transition-colors">
                <ImageIcon size={13} /> See a real example
              </button>
              <button data-testid="guide-replay-phase1" onClick={() => setGuideN((n) => n + 1)}
                className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500 hover:text-slate-700 transition-colors">
                <PlayCircle size={13} /> Show me how
              </button>
              <button data-testid="ai-suggest-button" onClick={suggest} disabled={busyAi}
                className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full border border-[#4F46E5] brand-pink hover:bg-[#EEF2FF] transition-colors">
                {busyAi ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} AI suggest
              </button>
            </div>
          )}
        </div>

        <div ref={ref} className="relative bg-white rounded-2xl border border-slate-200 p-6 pr-36">
          <div className="space-y-2.5">
            {units.map((u) => {
              const rn = stagedRenames[u.id] !== undefined;
              return (
                <div key={u.id} data-conn-id={`unit-${u.id}`} data-testid={`unit-row-${u.name}`} onClick={() => clickUnit(u.id)}
                  className={`conn-row flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 ${sel === u.id ? "selected" : ""} ${locked ? "cursor-default" : ""} ${rn ? "ring-2 ring-emerald-400 ring-offset-1" : ""}`}>
                  <div data-conn-id={`row-${u.id}`} className="flex items-center gap-3 min-w-0">
                    <span className="h-8 px-2.5 min-w-[32px] max-w-[180px] rounded-lg bg-slate-900 text-white font-display font-bold text-xs flex items-center justify-center truncate">{u.name}</span>
                    {editingId === u.id ? (
                      <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <input data-testid={`rename-input-${u.name}`} autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && stageRename(u.id)}
                          className="text-sm border border-[#4F46E5] rounded-md px-2 py-1 w-40 focus:outline-none" />
                        <button data-testid={`rename-save-${u.name}`} onClick={() => stageRename(u.id)} className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center"><Check size={12} /></button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`text-sm truncate ${rn ? "text-emerald-700 font-semibold" : "text-slate-600"}`}>{unitName(u.id)}</span>
                        {rn && <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">pending</span>}
                        {!locked && (
                          <button data-testid={`rename-unit-${u.name}`} data-conn-id={`pencil-${u.id}`} onClick={(e) => { e.stopPropagation(); setEditingId(u.id); setEditName(unitName(u.id)); }}
                            className="text-slate-300 hover:brand-pink transition-colors shrink-0"><Pencil size={12} /></button>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isDraft && !locked && units.length > 1 && (
                      <button data-testid={`remove-unit-${u.name}`} onClick={(e) => { e.stopPropagation(); removeUnit(u); }}
                        disabled={removingId === u.id}
                        title="Remove this unit"
                        className="text-slate-300 hover:text-red-500 transition-colors disabled:opacity-50">
                        {removingId === u.id ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} />}
                      </button>
                    )}
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                  </div>
                </div>
              );
            })}
          </div>
          <ThreadLayer containerRef={ref} links={threadLinks} />
          {!locked && units.length >= 2 && !property.phase1_done && (
            <CursorGuide containerRef={ref} fromId={`unit-${units[0].id}`} toId={`unit-${units[1].id}`} loop
              storageKey="atiti_guide_phase1_v2" playSignal={guideN}
              messages={["Tap the first unit…", "…then tap the unit that's the SAME physical space", "Connected! Tap ✕ on a thread to undo, or hit Save Changes."]}
              renamePencilId={`pencil-${units[0].id}`}
              renameRowId={`row-${units[0].id}`}
              renameSampleName="Sea-view Balcony"
              renameMessages={[
                "Now watch — tap the pencil to give this unit your own name",
                "Type anything memorable to you…",
                "All edits stay pending until you click Save Changes.",
              ]} />
          )}
        </div>
        {sel && <p className="text-xs brand-pink font-semibold mt-3">Unit {unitName(sel)} selected — tap another unit to connect, or tap it again to cancel.</p>}
        {stagedDeletedLinks.size > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold text-slate-500">Pending removes:</span>
            {Array.from(stagedDeletedLinks).map((lid) => {
              const l = links.find((x) => x.id === lid);
              if (!l) return null;
              return (
                <button key={lid} onClick={() => undoDeleteLink(lid)}
                  data-testid={`undo-delete-link-${lid}`}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100 flex items-center gap-1">
                  <Undo2 size={10} /> {unitName(l.a)} ↔ {unitName(l.b)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Add unit — allowed freely on draft; on live properties the
            backend gates behind the add-on billing flow. Hidden entirely
            once Phase 1 is locked unless the host enabled admin override. */}
        {!locked && (
          <div data-testid="add-unit-card" className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-display font-bold text-sm text-slate-900 flex items-center gap-2"><Plus size={14} className="brand-pink" /> Add a unit</h3>
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              {isDraft
                ? "Give the next bedroom / room / villa its primary name — e.g. \"Garden Suite\". You can rename it later with the pencil icon."
                : "Property is live. Adding a unit here charges your add-on plan and auto-provisions a new room on every connected OTA within a minute."}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input data-testid="add-unit-name-input" value={addingName}
                onChange={(e) => setAddingName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addUnit()}
                placeholder={isDraft ? "e.g. Garden Suite (optional)" : "e.g. Garden Suite"}
                className="flex-1 min-w-0 text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
              <button data-testid="add-unit-button" onClick={addUnit} disabled={addingBusy}
                className="btn-brand rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 shrink-0">
                {addingBusy ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} Add
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-display font-bold text-sm text-slate-900">Blocking groups</h3>
          {displayLinks.length === 0 && stagedNewLinks.length === 0 ? <p className="text-xs text-slate-400 mt-2">None yet. If no units share a space, just continue.</p> : (
            <ul className="mt-3 space-y-2">
              {displayLinks.map((l, i) => (
                <li key={l.id} className="flex items-center gap-2 text-sm text-slate-600">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CONN_COLORS[i % CONN_COLORS.length] }} />
                  {unitName(l.a)} ↔ {unitName(l.b)} {l.ai && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#EEF2FF] brand-pink">AI · confirmed</span>}
                </li>
              ))}
              {stagedNewLinks.map((l, i) => (
                <li key={`new-${i}`} className="flex items-center gap-2 text-sm text-emerald-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  {unitName(l.a)} ↔ {unitName(l.b)} <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200">pending add</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {locked ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 rounded-xl px-4 py-3"><Lock size={13} /> Phase 1 is locked. Continue to listing mapping.</div>
            {can_edit_setup && property.status === "active" && (
              <button data-testid="phase1-unlock-button" onClick={() => { setAdminOverride(true); toast.info("Editing enabled — make all your changes, then hit Save Changes."); }}
                className="w-full rounded-full py-2.5 text-xs font-semibold border border-[#4F46E5] brand-pink hover:bg-[#EEF2FF] flex items-center justify-center gap-1.5">
                <Pencil size={12} /> Edit connections & unit names (admin)
              </button>
            )}
          </div>
        ) : doneBtn ? (
          <div className="flex flex-col gap-2 sticky bottom-4">
            <div className={`flex items-center gap-2 text-xs rounded-xl px-4 py-3 border ${dirty ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-100 border-slate-200"}`}>
              <Pencil size={13} /> <span>{dirty ? `${stagedCount} change${stagedCount === 1 ? "" : "s"} pending — click Save Changes to broadcast to every OTA.` : "Admin edit mode. Make all your changes, then click Save Changes."}</span>
            </div>
            <button data-testid="phase1-save-changes-button" onClick={saveAll} disabled={busy || !dirty}
              className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save {dirty ? `${stagedCount} change${stagedCount === 1 ? "" : "s"}` : "changes"}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="phase1-discard-button" onClick={discardAll} disabled={busy || !dirty}
                className="rounded-full py-2 text-[11px] font-semibold border border-slate-300 text-slate-600 hover:border-slate-500 disabled:opacity-40 flex items-center justify-center gap-1.5">
                <Undo2 size={12} /> Discard
              </button>
              <button data-testid="phase1-lock-button" onClick={() => { if (dirty) { toast.error("Save or discard your pending edits first"); return; } setAdminOverride(false); toast.success("Locked back — Phase 1 is read-only again."); }}
                className="rounded-full py-2 text-[11px] font-semibold border border-slate-300 text-slate-600 hover:border-slate-500 flex items-center justify-center gap-1.5">
                <Lock size={12} /> Done editing
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2 sticky bottom-4">
            {dirty && (
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <Pencil size={13} /> <span>{stagedCount} change{stagedCount === 1 ? "" : "s"} pending — click Save Changes below, then finish Phase 1.</span>
              </div>
            )}
            {dirty && (
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="phase1-save-changes-button" onClick={saveAll} disabled={busy}
                  className="rounded-full py-2 text-xs font-semibold border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-1.5 disabled:opacity-50">
                  {busy ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save {stagedCount}
                </button>
                <button data-testid="phase1-discard-button" onClick={discardAll} disabled={busy}
                  className="rounded-full py-2 text-xs font-semibold border border-slate-300 text-slate-600 hover:border-slate-500 flex items-center justify-center gap-1.5 disabled:opacity-40">
                  <Undo2 size={12} /> Discard
                </button>
              </div>
            )}
            <button data-testid="phase1-complete-button" onClick={complete} disabled={busy}
              className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Lock size={15} />} {dirty ? "Save & finish Phase 1" : "Finish Phase 1 & unlock mapping"} <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>

      {showExample && (
        <div data-testid="example-modal" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowExample(false)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between px-6 pt-5 pb-3">
              <div>
                <h3 className="font-display font-bold text-slate-900">A real host did this — so can you</h3>
                <p className="text-xs text-slate-500 mt-1">Maan Tower by Atiti Homestays, Patna · 10 units connected in minutes. Every colored thread = two entries that are the same physical space.</p>
              </div>
              <button data-testid="close-example-button" onClick={() => setShowExample(false)} className="text-slate-400 hover:text-slate-700 shrink-0 ml-3"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto px-6">
              <img src="https://customer-assets-agu9un31.emergentagent.net/job_property-pms-1/artifacts/8x4pucho_IMG_1171.webp"
                alt="Real example: Maan Tower units connected with colored threads" className="w-full rounded-2xl border border-slate-200" />
            </div>
            <div className="px-6 py-4">
              <button data-testid="try-it-myself-button" onClick={() => setShowExample(false)}
                className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
                Got it — I'll do it myself <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {isHotel && (
        <div className="lg:col-span-3">
          <UnitTypesSection pid={property.id} units={units} unitTypes={unit_types} reload={reload} />
        </div>
      )}
    </div>
  );
}
